import { create } from 'zustand';

import { allStores } from './constants/allStores';
import { extractInitialState } from './extractInitialState';
import { persistStoreDuringHMR } from './persistStoreDuringHMR';
import { CreateStoreOptions } from './types/CreateStoreOptions';
import { Store } from './types/Store';
import { withMiddleware } from './withMiddleware';
import { withReset } from './withReset';

/**
 * Creates a Zustand store from a base `StateCreator`, with optional middleware,
 * devtools support, HMR persistence, and a `.reset()` method.
 */
export const createStore = <T>({
  creator,
  id,
  ignoreReset = false,
  middlewares = {},
  persistHMR = false,
}: CreateStoreOptions<T>): Store<T> => {
  // Generate the store's initial state before middleware is applied
  const initialState = extractInitialState(create(creator).getState());

  // Wrap the base store creator with middleware (ex. devtools, persist)
  const storeCreator = withMiddleware(creator, id, middlewares);

  // Create the actual Zustand store from the wrapped creator
  const boundStore = create(storeCreator) as Store<T>;

  // Attach a `.reset()` method for restoring the initial state (used for `resetStore`)
  withReset(boundStore, initialState, ignoreReset);

  // Rehydrate the store during hot module replacement if enabled
  if (persistHMR) {
    persistStoreDuringHMR(id, boundStore);
  }

  // Register the store in the global store registry (used for `resetAllStores`)
  allStores.push(boundStore);

  return boundStore;
};
